/**
 * Renderiza bloques <RICH_DATA>...</RICH_DATA> que el LLM embebe en su respuesta.
 * Soporta: table, kpi, chart + card/approval/diff/timeline/warning/success/error/metric/image/progress/empty.
 */
import {
  AlertTriangle,
  CheckCircle2,
  ImageIcon,
  Inbox,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export type RichBlockType =
  | "table"
  | "kpi"
  | "chart"
  | "card"
  | "approval"
  | "diff"
  | "timeline"
  | "warning"
  | "success"
  | "error"
  | "metric"
  | "image"
  | "progress"
  | "empty";

export interface RichBlock {
  type: RichBlockType;
  title?: string;
  // table
  headers?: string[];
  rows?: (string | number)[][];
  // kpi / metric
  items?: {
    label: string;
    value: string;
    trend?: string;
    color?: "green" | "yellow" | "red" | "blue";
  }[];
  label?: string;
  value?: string | number;
  unit?: string;
  // chart
  chartType?: "bar" | "line";
  data?: { x: string; y: number }[];
  // card / messages
  body?: string;
  description?: string;
  subtitle?: string;
  // approval
  status?: string;
  // diff
  before?: string;
  after?: string;
  // timeline
  events?: { time?: string; label: string; detail?: string }[];
  // image
  src?: string;
  alt?: string;
  // progress
  percent?: number;
  // empty
  message?: string;
}

interface RichContentProps {
  block: RichBlock;
  className?: string;
}

export function RenataRichContent({ block, className }: RichContentProps) {
  return (
    <div className={cn("mt-2 rounded-xl border bg-background/60 overflow-hidden text-xs", className)}>
      {block.title && (
        <div className="px-3 py-1.5 border-b bg-muted/40 font-medium text-foreground/70 text-[11px] uppercase tracking-wide">
          {block.title}
        </div>
      )}
      {block.type === "table" && block.rows && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            {block.headers && (
              <thead>
                <tr className="border-b">
                  {block.headers.map((h, i) => (
                    <th key={i} className="px-3 py-1.5 font-semibold text-foreground/60 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className={cn("border-b last:border-0", i % 2 === 0 ? "" : "bg-muted/20")}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-1.5 whitespace-nowrap text-foreground/80">
                      {String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {block.type === "kpi" && block.items && (
        <div className="grid grid-cols-2 gap-px bg-border">
          {block.items.map((item, i) => (
            <div key={i} className="bg-background px-3 py-2 flex flex-col gap-0.5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{item.label}</span>
              <span
                className={cn(
                  "font-bold text-sm tabular-nums",
                  item.color === "green" && "text-emerald-600 dark:text-emerald-400",
                  item.color === "yellow" && "text-amber-600 dark:text-amber-400",
                  item.color === "red" && "text-red-600 dark:text-red-400",
                  (!item.color || item.color === "blue") && "text-foreground",
                )}
              >
                {item.value}
              </span>
              {item.trend && (
                <span className="text-[10px] text-muted-foreground">{item.trend}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {block.type === "chart" && block.data && block.data.length > 0 && (
        <SimpleBarChart data={block.data} />
      )}
      {block.type === "card" && (
        <div className="px-3 py-2 space-y-0.5">
          {block.subtitle && (
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{block.subtitle}</p>
          )}
          <p className="text-sm font-medium text-foreground">{block.body ?? block.description ?? ""}</p>
        </div>
      )}
      {block.type === "approval" && (
        <div className="flex items-center gap-2 px-3 py-2">
          <Badge variant="secondary" className="font-normal">
            {block.status ?? "pendiente"}
          </Badge>
          <span className="text-foreground/80">{block.body ?? block.description ?? "Requiere aprobación"}</span>
        </div>
      )}
      {block.type === "diff" && (
        <div className="grid gap-px bg-border sm:grid-cols-2">
          <div className="bg-red-50/60 px-3 py-2 dark:bg-red-950/20">
            <p className="mb-1 text-[10px] font-medium text-red-700 dark:text-red-400">Antes</p>
            <pre className="whitespace-pre-wrap break-words text-foreground/80">{block.before ?? "—"}</pre>
          </div>
          <div className="bg-emerald-50/60 px-3 py-2 dark:bg-emerald-950/20">
            <p className="mb-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">Después</p>
            <pre className="whitespace-pre-wrap break-words text-foreground/80">{block.after ?? "—"}</pre>
          </div>
        </div>
      )}
      {block.type === "timeline" && block.events && (
        <ul className="space-y-2 px-3 py-2">
          {block.events.map((ev, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
              <div className="min-w-0">
                <p className="font-medium text-foreground/90">{ev.label}</p>
                {ev.detail && <p className="text-muted-foreground">{ev.detail}</p>}
                {ev.time && <p className="text-[10px] text-muted-foreground">{ev.time}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {(block.type === "warning" || block.type === "success" || block.type === "error") && (
        <StatusLine type={block.type} text={block.body ?? block.description ?? block.message ?? ""} />
      )}
      {block.type === "metric" && (
        <div className="px-3 py-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {block.label ?? "Métrica"}
          </p>
          <p className="text-lg font-bold tabular-nums">
            {block.value ?? "—"}
            {block.unit ? <span className="ml-1 text-xs font-normal text-muted-foreground">{block.unit}</span> : null}
          </p>
        </div>
      )}
      {block.type === "image" && (
        <div className="px-3 py-2">
          {block.src ? (
            <img
              src={block.src}
              alt={block.alt ?? block.title ?? "Imagen"}
              className="max-h-40 w-full rounded-lg object-cover"
            />
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <ImageIcon className="h-4 w-4" />
              <span>{block.alt ?? "Sin imagen"}</span>
            </div>
          )}
        </div>
      )}
      {block.type === "progress" && (
        <div className="space-y-1.5 px-3 py-2">
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{block.label ?? "Progreso"}</span>
            <span className="tabular-nums">{Math.max(0, Math.min(100, block.percent ?? 0))}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-violet-500 transition-all"
              style={{ width: `${Math.max(0, Math.min(100, block.percent ?? 0))}%` }}
            />
          </div>
        </div>
      )}
      {block.type === "empty" && (
        <div className="flex flex-col items-center gap-1 px-3 py-4 text-center text-muted-foreground">
          <Inbox className="h-4 w-4" />
          <p>{block.message ?? block.body ?? "Sin datos"}</p>
        </div>
      )}
    </div>
  );
}

function StatusLine({
  type,
  text,
}: {
  type: "warning" | "success" | "error";
  text: string;
}) {
  const Icon = type === "success" ? CheckCircle2 : type === "error" ? XCircle : AlertTriangle;
  return (
    <div
      className={cn(
        "flex items-start gap-2 px-3 py-2",
        type === "success" && "bg-emerald-50/70 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300",
        type === "warning" && "bg-amber-50/70 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
        type === "error" && "bg-red-50/70 text-red-800 dark:bg-red-950/30 dark:text-red-300",
      )}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <p className="leading-snug">{text || (type === "success" ? "OK" : type === "error" ? "Error" : "Atención")}</p>
    </div>
  );
}

function SimpleBarChart({ data }: { data: { x: string; y: number }[] }) {
  const max = Math.max(...data.map((d) => d.y), 1);
  const h = 80;
  const barW = Math.max(14, Math.floor(220 / data.length) - 4);

  return (
    <div className="px-3 py-2">
      <svg
        viewBox={`0 0 ${data.length * (barW + 4)} ${h + 20}`}
        className="w-full overflow-visible"
        style={{ maxHeight: 100 }}
      >
        {data.map((d, i) => {
          const barH = Math.max(2, (d.y / max) * h);
          const x = i * (barW + 4);
          const y = h - barH;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={barH}
                rx={2}
                className="fill-violet-500/70"
              />
              <text
                x={x + barW / 2}
                y={h + 13}
                textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: 8 }}
              >
                {d.x}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Parsea bloques <RICH_DATA>...</RICH_DATA> del contenido LLM. */
export function parseRichBlocks(content: string): { text: string; blocks: RichBlock[] } {
  const blocks: RichBlock[] = [];
  const cleaned = content.replace(/<RICH_DATA>([\s\S]*?)<\/RICH_DATA>/g, (_, json: string) => {
    try {
      const block = JSON.parse(json.trim()) as RichBlock;
      if (block.type) blocks.push(block);
    } catch {
      // ignore malformed blocks
    }
    return "";
  }).trim();
  return { text: cleaned, blocks };
}
